import { useEffect, useRef, useState, useContext } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AppContext } from '../../../../context/App/AppContext';
import videojs from 'video.js';
import qualityLevels from 'videojs-contrib-quality-levels';
import hlsQualitySelector from 'videojs-hls-quality-selector';
import {
  getVideoProgressByVideoId,
  saveVideoProgressByVideoId,
  editVideoProgressByVideoId,
  refreshFetchFunctions,
  saveLastWatchVideoId
} from '../../../../apiFunctions/apiFunctions';
import classes from './VideoPlayer.module.scss';
import 'video.js/dist/video-js.css';

videojs.registerPlugin('qualityLevels', qualityLevels);
videojs.registerPlugin('hlsQualitySelector', hlsQualitySelector);

export const VideoPlayer = ({ sources, disableScrubbing = true, videoId }) => {
  const { userData } = useContext(AppContext);
  const isRoleUser = userData.role === 'user';
  let passPurchase = null;
  if (isRoleUser) {
    passPurchase = userData.products.filter((elm) => elm.productType === 'videoPass');
  }
  const { data: videoProgress, isLoading } = useQuery({
    queryKey: ['videoProgress', videoId],
    queryFn: () => getVideoProgressByVideoId(videoId, passPurchase[0]._id),
    enabled: !!videoId && isRoleUser
  });

  const { mutate: saveVideoProgress } = useMutation(
    (data) =>
      videoProgress && videoProgress._id
        ? editVideoProgressByVideoId(videoProgress._id, {
            progress: data.progress,
            playBackPositionTime: data.playBackPositionTime
          })
        : saveVideoProgressByVideoId(data),
    {
      onSuccess: () => {
        refreshFetchFunctions(['videoProgress']);
      }
    }
  );

  const { mutate: saveLastWatchedVideo } = useMutation(() => saveLastWatchVideoId(passPurchase[0]._id, videoId));

  const videoRef = useRef(null);
  const VideoHtml = () => (
    <div data-vjs-player className={classes.videoGrommetReset}>
      <video data-testid="vp" ref={videoRef} className="video-js vjs-big-play-centered" />
    </div>
  );

  useEffect(() => {
    const videoElement = videoRef.current;
    let player;

    const saveVideoProgressCall = () => {
      if (isRoleUser) {
        const playbackPosition = player.currentTime();
        // Save only if user watched something AND video is not fully completed
        if (playbackPosition > 0 && playbackPosition < player.duration()) {
          saveVideoProgress(getVideoObject());
        }
      }
    };

    const getVideoObject = () => {
      return {
        progress: ((player.currentTime() / player.duration()) * 100).toFixed(2),
        video: videoId,
        totalDuration: player.duration(),
        playBackPositionTime: player.currentTime(), // Updated to get current time
        passPurchaseId: passPurchase[0]._id
      };
    };

    if (videoElement) {
      player = videojs(videoElement, {
        html5: {
          vhs: {
            overrideNative: true
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
          nativeTextTracks: false,
          hls: {
            overrideNative: true
          }
        },
        controls: true,
        responsive: true,
        fluid: true,
        preload: 'auto',
        controlBar: { pictureInPictureToggle: false },
        plugins: {
          qualityLevels: {},
          hlsQualitySelector: {}
        },
        sources
      });

      if (!isLoading) {
        const savedPosition =
          videoProgress && videoProgress.playBackPositionTime ? videoProgress.playBackPositionTime : 0;

        if (savedPosition) {
          player.currentTime(parseFloat(savedPosition));
        }
      }

      player.on('ended', () => {
        if (isRoleUser) {
          saveLastWatchedVideo();
          saveVideoProgress(getVideoObject());
        }
      });

      player.on('pause', function () {
        saveVideoProgressCall();
      });

      window.addEventListener('beforeunload', async (event) => {
        saveVideoProgressCall();
      });

      if (disableScrubbing) {
        player.controlBar.progressControl.disable();
      }
    }

    return async () => {
      if (player) {
        if (!player.paused()) {
          await saveVideoProgressCall();
        }
        player.dispose();
      }
    };
  }, [sources, disableScrubbing, videoProgress]);

  return (
    <div className={classes.videoGrommetReset}>
      <VideoHtml />
    </div>
  );
};

export default VideoPlayer;
